import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AuthActionError,
  authErrorToTranslationKey,
  sendPasswordResetEmail,
} from "@/features/auth/api/authClient";
import {
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
} from "@/features/auth/validation/authSchemas";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values: ForgotPasswordFormValues) {
    setMessage(null);
    setSubmitError(null);

    try {
      await sendPasswordResetEmail(values);
      setMessage(t("auth.forgotPassword.success"));
    } catch (error) {
      if (error instanceof AuthActionError) {
        setSubmitError(t(authErrorToTranslationKey(error.code)));
        return;
      }

      setSubmitError(t("auth.errors.generic"));
    }
  }

  return (
    <AuthLayout
      eyebrow={t("auth.meta.eyebrow")}
      title={t("auth.forgotPassword.title")}
      subtitle={t("auth.forgotPassword.subtitle")}
    >
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <Label htmlFor="forgot-email">{t("auth.fields.email")}</Label>
          <Input id="forgot-email" type="email" autoComplete="email" {...form.register("email")} />
          {form.formState.errors.email ? (
            <FormMessage>{t(form.formState.errors.email.message ?? "auth.errors.generic")}</FormMessage>
          ) : null}
        </div>

        {message ? <FormMessage className="text-olive">{message}</FormMessage> : null}
        {submitError ? <FormMessage>{submitError}</FormMessage> : null}

        <Button className="w-full" type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("common.saving") : t("auth.forgotPassword.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
